import React, { lazy, Suspense, useEffect, useState } from "react";
import { Routes, Route, useLocation } from "react-router-dom";
import { AuthProvider } from "./utils/AuthContext";
import Header from "./components/Header";
import RouteLoadingFallback from "./components/RouteLoadingFallback";
import DeferredFooterLoader from "./components/DeferredFooterLoader";
import UiFeedbackHost from "./components/UiFeedbackHost";
import AuthRouteGate from "./components/AuthRouteGate";

/*
 * Страницы загружаются только при переходе на соответствующий маршрут.
 * Это не даёт форуму, блогу, сообщениям, Leaflet и десяткам категорий
 * попадать в стартовый JavaScript-бандл одновременно.
 */
const MainPage = lazy(() => import("./pages/MainPage"));
const DogsPage = lazy(() => import("./pages/DogsPage"));
const CatsPage = lazy(() => import("./pages/CatsPage"));
const BirdsPage = lazy(() => import("./pages/BirdsPage"));
const RodentsPage = lazy(() => import("./pages/RodentsPage"));
const ReptilesPage = lazy(() => import("./pages/ReptilesPage"));
const AquariumPage = lazy(() => import("./pages/AquariumPage"));
const InsectsPage = lazy(() => import("./pages/InsectsPage"));
const LostFoundPage = lazy(() => import("./pages/LostFoundPage"));
const MatingPage = lazy(() => import("./pages/MatingPage"));

const FarmPage = lazy(() => import("./pages/FarmPage"));
const CowsPage = lazy(() => import("./pages/farm/CowsPage"));
const SheepPage = lazy(() => import("./pages/farm/SheepPage"));
const FurPage = lazy(() => import("./pages/farm/FurPage"));
const HorsesPage = lazy(() => import("./pages/farm/HorsesPage"));
const PigsPage = lazy(() => import("./pages/farm/PigsPage"));
const FishPage = lazy(() => import("./pages/farm/FishPage"));
const BeesPage = lazy(() => import("./pages/farm/BeesPage"));
const PoultryPage = lazy(() => import("./pages/farm/PoultryPage"));
const GoatsPage = lazy(() => import("./pages/farm/GoatsPage"));
const OtherFarmPage = lazy(() => import("./pages/farm/OtherFarmPage"));

const ZooBusinessPage = lazy(() => import("./pages/ZooBusinessPage"));
const ShopsPage = lazy(() => import("./pages/zooBusiness/ShopsPage"));
const PharmacyPage = lazy(() => import("./pages/zooBusiness/PharmacyPage"));
const ClinicsPage = lazy(() => import("./pages/zooBusiness/ClinicsPage"));
const GroomingPage = lazy(() => import("./pages/zooBusiness/GroomingPage"));
const OnlinePage = lazy(() => import("./pages/zooBusiness/OnlinePage"));
const DeliveryPage = lazy(() => import("./pages/zooBusiness/DeliveryPage"));
const WalkPage = lazy(() => import("./pages/zooBusiness/WalkPage"));
const BoardingPage = lazy(() => import("./pages/zooBusiness/BoardingPage"));
const TrainingPage = lazy(() => import("./pages/zooBusiness/TrainingPage"));
const PhotoPage = lazy(() => import("./pages/zooBusiness/PhotoPage"));
const KennelsPage = lazy(() => import("./pages/zooBusiness/KennelsPage"));
const NurseryPage = lazy(() => import("./pages/zooBusiness/NurseryPage"));
const SheltersPage = lazy(() => import("./pages/zooBusiness/SheltersPage"));

const ForumPage = lazy(() => import("./pages/ForumPage"));
const ForumSectionPage = lazy(() => import("./pages/ForumSectionPage"));
const ForumTopicPage = lazy(() => import("./pages/ForumTopicPage"));

const BlogPage = lazy(() => import("./pages/blog/BlogPage"));
const BlogPostPage = lazy(() => import("./pages/blog/BlogPostPage"));

const AdminPage = lazy(() => import("./pages/admin/AdminPage"));
const Login = lazy(() => import("./pages/Login"));
const UserMap = lazy(() => import("./pages/MyMapPage"));
const EmailSignInPage = lazy(() => import("./pages/EmailSignInPage"));
const MessagesPage = lazy(() => import("./pages/MessagesPage"));
const DonatePage = lazy(() => import("./pages/DonatePage"));
const InfoPage = lazy(() => import("./pages/InfoPage"));
const PrivacyPolicyPage = lazy(() => import("./pages/PrivacyPolicyPage"));
const SearchResultsPage = lazy(() => import("./pages/SearchResultsPage"));

/* Тяжёлые модальные окна тоже не нужны до фактического открытия. */
const Register = lazy(() => import("./components/Register"));
const RegisterModal = lazy(() => import("./components/RegisterModal"));
const EmailSignInHandler = lazy(() =>
  import("./components/EmailSignInHandler")
);
const FullMarkerModal = lazy(() =>
  import("./components/FullMarkerModal")
);
const NotificationsModal = lazy(() =>
  import("./components/NotificationsModal")
);

function App() {
  const location = useLocation();

  const isMessagesRoute =
    location.pathname === '/messages' ||
    location.pathname.startsWith('/messages/');

  useEffect(() => {
    /*
     * На телефоне мессенджер работает как отдельный экран.
     * Класс нужен CSS, чтобы мобильная глобальная шапка
     * не отнимала высоту у поля ввода и не прятала его
     * за нижней границей viewport.
     */
    document.body.classList.toggle(
      'mapapet-messages-route',
      isMessagesRoute
    );

    return () => {
      document.body.classList.remove(
        'mapapet-messages-route'
      );
    };
  }, [isMessagesRoute]);

  const [registerOpen, setRegisterOpen] = useState(false);

  const [isFullMarkerOpen, setIsFullMarkerOpen] = useState(false);
  const [selectedMarker, setSelectedMarker] = useState(null);
  const [replyContext, setReplyContext] = useState(null);

  const [showNotifications, setShowNotifications] = useState(false);

  useEffect(() => {
    const handler = async (event) => {
      const { markerId } = event.detail || {};
      if (!markerId) return;

      try {
        const [
          { db },
          { doc, getDoc },
        ] = await Promise.all([
          import("./firebaseDbClient"),
          import("firebase/firestore"),
        ]);

        const snap = await getDoc(doc(db, 'ads', markerId));
        if (!snap.exists()) return;

        const x = snap.data();

        setSelectedMarker({
          id: snap.id,
          ...x,
          lat: parseFloat(x.lat),
          lng: parseFloat(x.lng),
        });

        setIsFullMarkerOpen(true);
      } catch (e) {
        console.error(e);
      }
    };

    window.addEventListener('open-marker-from-notification', handler);
    return () => window.removeEventListener('open-marker-from-notification', handler);
  }, []);

  return (
    <AuthProvider>
      <>
        <UiFeedbackHost />
        <Header />

        {registerOpen && (
          <Suspense fallback={null}>
            <RegisterModal
              isOpen={registerOpen}
              onClose={() => setRegisterOpen(false)}
            >
              <Register onClose={() => setRegisterOpen(false)} />
            </RegisterModal>
          </Suspense>
        )}

        <Suspense fallback={<RouteLoadingFallback />}>
          <>
          <Routes>
          {/* MAIN */}
          <Route path="/" element={<MainPage />} />

          {/* BASIC CATEGORIES */}
          <Route path="/dogs" element={<DogsPage />} />
          <Route path="/cats" element={<CatsPage />} />
          <Route path="/birds" element={<BirdsPage />} />
          <Route path="/rodents" element={<RodentsPage />} />
          <Route path="/reptiles" element={<ReptilesPage />} />
          <Route path="/aquarium" element={<AquariumPage />} />
          <Route path="/insects" element={<InsectsPage />} />
          <Route path="/lostfound" element={<LostFoundPage />} />
<Route path="/mating" element={<MatingPage />} />

          {/* FARM */}
          <Route path="/farm" element={<FarmPage />} />

          {/* Канонические роуты farm */}
          <Route path="/farm/cows" element={<CowsPage />} />
          <Route path="/farm/horses" element={<HorsesPage />} />
          <Route path="/farm/pigs" element={<PigsPage />} />
          <Route path="/farm/sheep" element={<SheepPage />} />
          <Route path="/farm/goats" element={<GoatsPage />} />
          <Route path="/farm/poultry" element={<PoultryPage />} />
          <Route path="/farm/bees" element={<BeesPage />} />
          <Route path="/farm/fur" element={<FurPage />} />
          <Route path="/farm/fishFarm" element={<FishPage />} />
          <Route path="/farm/otherFarm" element={<OtherFarmPage />} />

          {/* Старые/временные алиасы farm для совместимости */}
          <Route path="/farm/fish" element={<FishPage />} />

          {/* ZOO BUSINESS */}
          <Route path="/zooBusiness" element={<ZooBusinessPage />} />

          {/* Канонические роуты zooBusiness */}
          <Route path="/zooBusiness/shops" element={<ShopsPage />} />
          <Route path="/zooBusiness/pharmacy" element={<PharmacyPage />} />
          <Route path="/zooBusiness/clinics" element={<ClinicsPage />} />
          <Route path="/zooBusiness/grooming" element={<GroomingPage />} />
          <Route path="/zooBusiness/onlineServices" element={<OnlinePage />} />
          <Route path="/zooBusiness/transport" element={<DeliveryPage />} />
          <Route path="/zooBusiness/walking" element={<WalkPage />} />
          <Route path="/zooBusiness/boarding" element={<BoardingPage />} />
          <Route path="/zooBusiness/training" element={<TrainingPage />} />
          <Route path="/zooBusiness/photo" element={<PhotoPage />} />
          <Route path="/zooBusiness/kennels" element={<KennelsPage />} />
		  <Route path="/zooBusiness/nursery" element={<NurseryPage />} />
          <Route path="/zooBusiness/shelters" element={<SheltersPage />} />
          <Route
            path="/admin"
            element={
              <AuthRouteGate>
                <AdminPage />
              </AuthRouteGate>
            }
          />

          {/* Старые/временные алиасы zooBusiness для совместимости */}
          <Route path="/zooBusiness/online" element={<OnlinePage />} />
          <Route path="/zooBusiness/delivery" element={<DeliveryPage />} />
          <Route path="/zooBusiness/walk" element={<WalkPage />} />

          {/* FORUM */}
          <Route path="/forum" element={<ForumPage />} />
          <Route path="/forum/section/:id" element={<ForumSectionPage />} />
          <Route path="/forum/thread/:id" element={<ForumTopicPage />} />

          {/* BLOG */}
          <Route path="/blog" element={<BlogPage />} />
          <Route path="/blog/:slug" element={<BlogPostPage />} />

          {/* PROJECT INFORMATION */}
          <Route path="/donate" element={<DonatePage />} />
          <Route path="/about" element={<InfoPage page="about" />} />
          <Route path="/contacts" element={<InfoPage page="contacts" />} />
          <Route path="/help" element={<InfoPage page="help" />} />
          <Route path="/rules" element={<InfoPage page="rules" />} />
          <Route path="/privacy" element={<PrivacyPolicyPage />} />
          <Route path="/search" element={<SearchResultsPage />} />

          {/* MESSAGES */}
          <Route
            path="/messages"
            element={
              <AuthRouteGate>
                <MessagesPage />
              </AuthRouteGate>
            }
          />
          <Route
            path="/messages/:chatId"
            element={
              <AuthRouteGate>
                <MessagesPage />
              </AuthRouteGate>
            }
          />

          {/* AUTH */}
          <Route path="/register" element={<Register />} />
          <Route path="/login" element={<Login />} />
          <Route
            path="/user-map"
            element={
              <AuthRouteGate>
                <UserMap />
              </AuthRouteGate>
            }
          />

          {/* EMAIL SIGN-IN */}
          <Route path="/email-signin" element={<EmailSignInHandler />} />
          <Route path="/email-signin-page" element={<EmailSignInPage />} />
          </Routes>

          {!isMessagesRoute && <DeferredFooterLoader />}
          </>
        </Suspense>

        {showNotifications && (
          <Suspense fallback={null}>
            <NotificationsModal
              isOpen={showNotifications}
              onClose={() => setShowNotifications(false)}
            />
          </Suspense>
        )}

        {isFullMarkerOpen && selectedMarker && (
          <Suspense fallback={null}>
            <FullMarkerModal
              marker={selectedMarker}
              onClose={() => {
                setIsFullMarkerOpen(false);
                setReplyContext(null);
              }}
              replyContext={replyContext}
            />
          </Suspense>
        )}
      </>
    </AuthProvider>
  );
}

export default App;